import {AxiosResponse} from 'axios';
import {CallDirection} from '../backend/enums/call-direction';
import {MediaType} from '../backend/enums/media-type';
import {StatisticsBinning} from '../backend/enums/statistics-binning';
import {DeviceWorkgroup} from '../backend/enums/device-workgroup';
import {TransmissionDirection} from '../backend/enums/transmission-direction';
import {ActiveDevicesCountEntitiesStatistics} from '../backend/responses/statistics/entities/active-devices-count';
import {ActiveInformalCaregiversCountEntitiesStatistics} from '../backend/responses/statistics/entities/active-informal-caregivers-count';
import {ActiveProfessionalCaregiversCountEntitiesStatistics} from '../backend/responses/statistics/entities/active-professional-caregivers-count';
import {ActiveSeniorsCountEntitiesStatistics} from '../backend/responses/statistics/entities/active-seniors-count';
import {CallsCountEntitiesStatistics} from '../backend/responses/statistics/entities/calls-count';
import {CallsEvolutionEntitiesStatistics} from '../backend/responses/statistics/entities/calls-evolution';
import {InProgressInvitesCountEntitiesStatistics} from '../backend/responses/statistics/entities/in-progress-invites-count';
import {MostUsedApplicationsEntitiesStatistics} from '../backend/responses/statistics/entities/most-used-applications';
import {SystemUsageEntitiesStatistics} from '../backend/responses/statistics/entities/system-usage';
import {SystemUsagePerApplicationEntitiesStatistics} from '../backend/responses/statistics/entities/system-usage-per-application';
import {TopUsersUsingTheSystemEntitiesStatistics} from '../backend/responses/statistics/entities/top-users-using-the-system';
import {ApplicationLevelEvolutionUsersStatistics} from '../backend/responses/statistics/users/application-level-evolution';
import {CallsCountUsersStatistics} from '../backend/responses/statistics/users/calls-count';
import {InProgressInvitesCountUsersStatistics} from '../backend/responses/statistics/users/in-progress-invites-count';
import {InformalCaregiversCountUsersStatistics} from '../backend/responses/statistics/users/informal-caregivers-count';
import {LatestAccessUsersStatistics} from '../backend/responses/statistics/users/latest-access';
import {MediaCountUsersStatistics} from '../backend/responses/statistics/users/media-count';
import {MostUsedApplicationsUsersStatistics} from '../backend/responses/statistics/users/most-used-applications';
import {SystemUsageUsersStatistics} from '../backend/responses/statistics/users/system-usage';
import {SystemUsageAverageUsersStatistics} from '../backend/responses/statistics/users/system-usage-average';
import {SystemUsagePerApplicationUsersStatistics} from '../backend/responses/statistics/users/system-usage-per-application';
import {TopPreferencesUsersStatistics} from '../backend/responses/statistics/users/top-preferences';
import {VideoMessagesCountUsersStatistics} from '../backend/responses/statistics/users/video-messages-count';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';

export class StatisticsRepository {
  // Singleton Instance
  private static _instance: StatisticsRepository;

  // Private
  private logger: Logger;

  static get instance(): StatisticsRepository {
    if (!this._instance) {
      this._instance = new StatisticsRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('StatisticsRepository');
  }

  // Get the number of active seniors in an entity
  public async getActiveSeniorsCountInEntity(
    entityId: string
  ): Promise<ActiveSeniorsCountEntitiesStatistics> {
    // Get active seniors count from backend
    const response: AxiosResponse<ActiveSeniorsCountEntitiesStatistics> =
      await BackendApiManager.instance.get<ActiveSeniorsCountEntitiesStatistics>(
        `/statistics/entities/${entityId}/active-seniors-count`
      );

    // Return
    return response.data;
  }

  // Get the number of active professional caregivers in an entity
  public async getActiveProfessionalCaregiversCountInEntity(
    entityId: string
  ): Promise<ActiveProfessionalCaregiversCountEntitiesStatistics> {
    // Get active professional caregivers count from backend
    const response: AxiosResponse<ActiveProfessionalCaregiversCountEntitiesStatistics> =
      await BackendApiManager.instance.get<ActiveProfessionalCaregiversCountEntitiesStatistics>(
        `/statistics/entities/${entityId}/active-professional-caregivers-count`
      );

    // Return
    return response.data;
  }

  // Get the number of active informal caregivers in an entity
  public async getActiveInformalCaregiversCountInEntity(
    entityId: string
  ): Promise<ActiveInformalCaregiversCountEntitiesStatistics> {
    // Get active informal caregivers count from backend
    const response: AxiosResponse<ActiveInformalCaregiversCountEntitiesStatistics> =
      await BackendApiManager.instance.get<ActiveInformalCaregiversCountEntitiesStatistics>(
        `/statistics/entities/${entityId}/active-informal-caregivers-count`
      );

    // Return
    return response.data;
  }

  // Get the number of in progress invites in an entity
  public async getInProgressInvitesCountInEntity(
    entityId: string
  ): Promise<InProgressInvitesCountEntitiesStatistics> {
    // Get in progress invites count from backend
    const response: AxiosResponse<InProgressInvitesCountEntitiesStatistics> =
      await BackendApiManager.instance.get<InProgressInvitesCountEntitiesStatistics>(
        `/statistics/entities/${entityId}/in-progress-invites-count`
      );

    // Return
    return response.data;
  }

  // Get the number of calls in an entity
  public async getCallsCountInEntity(
    entityId: string,
    startDate: string,
    endDate: string,
    direction?: CallDirection
  ): Promise<CallsCountEntitiesStatistics> {
    // Url
    let url: string = `/statistics/entities/${entityId}/calls-count?startDate=${startDate}&endDate=${endDate}`;

    // Direction
    if (direction) {
      // Add to the url
      url += `&direction=${direction}`;
    }

    // Get calls count from backend
    const response: AxiosResponse<CallsCountEntitiesStatistics> =
      await BackendApiManager.instance.get<CallsCountEntitiesStatistics>(url);

    // Return
    return response.data;
  }

  // Get the number of active devices in an entity
  public async getActiveDevicesCountInEntity(
    entityId: string,
    workgroup?: DeviceWorkgroup
  ): Promise<ActiveDevicesCountEntitiesStatistics> {
    // Build url
    let url: string = `/statistics/entities/${entityId}/active-devices-count`;

    // Check workgroup
    if (workgroup) {
      url += `?deviceWorkgroup=${workgroup}`;
    }

    // Get active devices count from backend
    const response: AxiosResponse<ActiveDevicesCountEntitiesStatistics> =
      await BackendApiManager.instance.get<ActiveDevicesCountEntitiesStatistics>(url);

    // Return
    return response.data;
  }

  // Get the top users in an entity
  public async getTopUsersInEntity(
    entityId: string,
    startDate: string,
    endDate: string
  ): Promise<TopUsersUsingTheSystemEntitiesStatistics> {
    // Get top users from backend
    const response: AxiosResponse<TopUsersUsingTheSystemEntitiesStatistics> =
      await BackendApiManager.instance.get<TopUsersUsingTheSystemEntitiesStatistics>(
        `/statistics/entities/${entityId}/top-users-using-the-system?startDate=${startDate}&endDate=${endDate}`
      );

    // Return
    return response.data;
  }

  // Get the most used applications in an entity
  public async getMostUsedApplicationsInEntity(
    entityId: string,
    startDate: string,
    endDate: string,
    localize?: boolean
  ): Promise<MostUsedApplicationsEntitiesStatistics> {
    // Url
    let url: string = `/statistics/entities/${entityId}/most-used-applications?startDate=${startDate}&endDate=${endDate}`;

    // Localize
    if (typeof localize === 'boolean') {
      url += `&localize=${localize}`;
    }

    // Get most used applications from backend
    const response: AxiosResponse<MostUsedApplicationsEntitiesStatistics> =
      await BackendApiManager.instance.get<MostUsedApplicationsEntitiesStatistics>(url);

    // Return
    return response.data;
  }

  // Get the system usage in an entity
  public async getSystemUsageInEntity(
    entityId: string,
    startDate: string,
    endDate: string,
    binning?: StatisticsBinning
  ): Promise<SystemUsageEntitiesStatistics> {
    // Url
    let url: string = `/statistics/entities/${entityId}/system-usage?startDate=${startDate}&endDate=${endDate}`;

    // Binning
    if (binning) {
      url += `&binning=${binning}`;
    }

    // Get system usage from backend
    const response: AxiosResponse<SystemUsageEntitiesStatistics> =
      await BackendApiManager.instance.get<SystemUsageEntitiesStatistics>(url);

    // Return
    return response.data;
  }

  // Get the calls evolution in an entity
  public async getCallsEvolutionInEntity(
    entityId: string,
    startDate: string,
    endDate: string,
    binning?: StatisticsBinning
  ): Promise<CallsEvolutionEntitiesStatistics> {
    // Url
    let url: string = `/statistics/entities/${entityId}/calls-over-time?startDate=${startDate}&endDate=${endDate}`;

    // Binning
    if (binning) {
      url += `&binning=${binning}`;
    }

    // Get calls evolution from backend
    const response: AxiosResponse<CallsEvolutionEntitiesStatistics> =
      await BackendApiManager.instance.get<CallsEvolutionEntitiesStatistics>(url);

    // Return
    return response.data;
  }

  // Get the system usage per application in an entity
  public async getSystemUsagePerApplicationInEntity(
    entityId: string,
    startDate: string,
    endDate: string,
    localize?: boolean,
    binning?: StatisticsBinning
  ): Promise<SystemUsagePerApplicationEntitiesStatistics> {
    // Url
    let url: string = `/statistics/entities/${entityId}/system-usage-per-application?startDate=${startDate}&endDate=${endDate}`;

    // Localize
    if (typeof localize === 'boolean') {
      url += `&localize=${localize}`;
    }

    // Binning
    if (binning) {
      url += `&binning=${binning}`;
    }

    // Get system usage per application from backend
    const response: AxiosResponse<SystemUsagePerApplicationEntitiesStatistics> =
      await BackendApiManager.instance.get<SystemUsagePerApplicationEntitiesStatistics>(url);

    // Return
    return response.data;
  }

  // Get the number of informal caregivers for a user
  public async getInformalCaregiversCountForUser(
    userId: string
  ): Promise<InformalCaregiversCountUsersStatistics> {
    // Get informal caregivers count from backend
    const response: AxiosResponse<InformalCaregiversCountUsersStatistics> =
      await BackendApiManager.instance.get<InformalCaregiversCountUsersStatistics>(
        `/statistics/users/${userId}/informal-caregivers-count`
      );

    // Return
    return response.data;
  }

  // Get the number of in progress invites for a user
  public async getInProgressInvitesCountForUser(
    userId: string
  ): Promise<InProgressInvitesCountUsersStatistics> {
    // Get in progress invites count from backend
    const response: AxiosResponse<InProgressInvitesCountUsersStatistics> =
      await BackendApiManager.instance.get<InProgressInvitesCountUsersStatistics>(
        `/statistics/users/${userId}/in-progress-invites-count`
      );

    // Return
    return response.data;
  }

  // Get the system usage average for a user
  public async getSystemAverageUsageForUser(
    userId: string,
    startDate: string,
    endDate: string
  ): Promise<SystemUsageAverageUsersStatistics> {
    // Get system usage average from backend
    const response: AxiosResponse<SystemUsageAverageUsersStatistics> =
      await BackendApiManager.instance.get<SystemUsageAverageUsersStatistics>(
        `/statistics/users/${userId}/system-usage-average?startDate=${startDate}&endDate=${endDate}`
      );

    // Return
    return response.data;
  }

  // Get the latest access for a user
  public async getLatestAccessForUser(userId: string): Promise<LatestAccessUsersStatistics> {
    // Get latest access from backend
    const response: AxiosResponse<LatestAccessUsersStatistics> =
      await BackendApiManager.instance.get<LatestAccessUsersStatistics>(
        `/statistics/users/${userId}/latest-access`
      );

    // Return
    return response.data;
  }

  // Get the number of calls for a user
  public async getCallsCountForUser(
    userId: string,
    startDate: string,
    endDate: string,
    direction?: CallDirection
  ): Promise<CallsCountUsersStatistics> {
    // Url
    let url: string = `/statistics/users/${userId}/calls-count?startDate=${startDate}&endDate=${endDate}`;

    // Direction
    if (direction) {
      // Add to the url
      url += `&direction=${direction}`;
    }

    // Get calls count from backend
    const response: AxiosResponse<CallsCountUsersStatistics> =
      await BackendApiManager.instance.get<CallsCountUsersStatistics>(url);

    // Return
    return response.data;
  }

  // Get the number of video messages for a user
  public async getVideoMessagesCountForUser(
    userId: string,
    direction: TransmissionDirection
  ): Promise<VideoMessagesCountUsersStatistics> {
    // Get video messages count from backend
    const response: AxiosResponse<VideoMessagesCountUsersStatistics> =
      await BackendApiManager.instance.get<VideoMessagesCountUsersStatistics>(
        `/statistics/users/${userId}/video-messages-count?direction=${direction}`
      );

    // Return
    return response.data;
  }

  // Get the number of media for a user
  public async getMediaCountForUser(
    userId: string,
    direction: TransmissionDirection,
    types?: Array<MediaType>
  ): Promise<MediaCountUsersStatistics> {
    // Url
    let url: string = `/statistics/users/${userId}/media-count?direction=${direction}`;

    // Types
    if (types) {
      for (let i = 0; i < types.length; i++) {
        url += `&types=${types[i]}`;
      }
    }

    // Get media count from backend
    const response: AxiosResponse<MediaCountUsersStatistics> =
      await BackendApiManager.instance.get<MediaCountUsersStatistics>(url);

    // Return
    return response.data;
  }

  // Get top preferences for a user
  public async getTopPreferencesForUser(
    userId: string,
    startDate: string,
    endDate: string,
    localize?: boolean
  ): Promise<TopPreferencesUsersStatistics> {
    // Url
    let url: string = `/statistics/users/${userId}/top-preferences?startDate=${startDate}&endDate=${endDate}`;

    // Localize
    if (typeof localize === 'boolean') {
      url += `&localize=${localize}`;
    }

    // Get top preferences from backend
    const response: AxiosResponse<TopPreferencesUsersStatistics> =
      await BackendApiManager.instance.get<TopPreferencesUsersStatistics>(url);

    // Return
    return response.data;
  }

  // Get most used applications for a user
  public async getMostUsedApplicationsForUser(
    userId: string,
    startDate: string,
    endDate: string,
    localize?: boolean
  ): Promise<MostUsedApplicationsUsersStatistics> {
    // Url
    let url: string = `/statistics/users/${userId}/most-used-applications?startDate=${startDate}&endDate=${endDate}`;

    // Localize
    if (typeof localize === 'boolean') {
      url += `&localize=${localize}`;
    }

    // Get most used applications from backend
    const response: AxiosResponse<MostUsedApplicationsUsersStatistics> =
      await BackendApiManager.instance.get<MostUsedApplicationsUsersStatistics>(url);

    // Return
    return response.data;
  }

  // Get the system usage for a user
  public async getSystemUsageForUser(
    userId: string,
    startDate: string,
    endDate: string,
    binning?: StatisticsBinning
  ): Promise<SystemUsageUsersStatistics> {
    // Url
    let url: string = `/statistics/users/${userId}/system-usage?startDate=${startDate}&endDate=${endDate}`;

    // Binning
    if (binning) {
      url += `&binning=${binning}`;
    }

    // Get system usage from backend
    const response: AxiosResponse<SystemUsageUsersStatistics> =
      await BackendApiManager.instance.get<SystemUsageUsersStatistics>(url);

    // Return
    return response.data;
  }

  // Get the system usage per application for a user
  public async getSystemUsagePerApplicationForUser(
    userId: string,
    startDate: string,
    endDate: string,
    applicationId?: string,
    localize?: boolean,
    binning?: StatisticsBinning
  ): Promise<SystemUsagePerApplicationUsersStatistics> {
    // Url
    let url: string = `/statistics/users/${userId}/system-usage-per-application?startDate=${startDate}&endDate=${endDate}`;

    // Localize
    if (typeof localize === 'boolean') {
      url += `&localize=${localize}`;
    }

    // Application id
    if (applicationId && applicationId.trim().length > 0) {
      url += `&applicationId=${applicationId}`;
    }

    // Binning
    if (binning) {
      url += `&binning=${binning}`;
    }

    // Get system usage per application from backend
    const response: AxiosResponse<SystemUsagePerApplicationUsersStatistics> =
      await BackendApiManager.instance.get<SystemUsagePerApplicationUsersStatistics>(url);

    // Return
    return response.data;
  }

  // Get the application level evolution for a user
  public async getApplicationLevelEvolutionForUser(
    userId: string,
    startDate: string,
    endDate: string,
    applicationId: string
  ): Promise<ApplicationLevelEvolutionUsersStatistics> {
    // Get application level evolution from backend
    const response: AxiosResponse<ApplicationLevelEvolutionUsersStatistics> =
      await BackendApiManager.instance.get<ApplicationLevelEvolutionUsersStatistics>(
        `/statistics/users/${userId}/application-level-evolution?startDate=${startDate}&endDate=${endDate}&applicationId=${applicationId}`
      );

    // Return
    return response.data;
  }
}
